import { v4 as uuidv4 } from 'uuid';
import type { SyncQueueItem, SyncResult, Referidor, Referido } from '@/types';
import { indexedDBService } from '@/services/database';
import { sheetsService } from '@/services/sheets';
import { SYNC_CONFIG } from '@/constants';

class SyncManager {
  private isSyncing = false;

  /**
   * Add an item to the sync queue
   */
  async addToQueue(
    item: Omit<SyncQueueItem, 'id' | 'timestamp' | 'status' | 'retryCount'>
  ): Promise<void> {
    const queueItem: SyncQueueItem = {
      id: uuidv4(),
      timestamp: Date.now(),
      status: 'pending',
      retryCount: 0,
      ...item,
    };

    await indexedDBService.addToSyncQueue(queueItem);
  }

  /**
   * Get count of pending items
   */
  async getPendingCount(): Promise<number> {
    return indexedDBService.getPendingSyncCount();
  }

  /**
   * Process the sync queue
   */
  async processQueue(): Promise<SyncResult> {
    if (this.isSyncing) {
      return { success: false, message: 'Sincronización en progreso' };
    }

    this.isSyncing = true;
    const results: { id: string; success: boolean; error?: unknown }[] = [];

    try {
      const pendingItems = await indexedDBService.getPendingSyncItems();

      if (pendingItems.length === 0) {
        return { success: true, message: 'No hay elementos pendientes', results: [] };
      }

      // Sort by timestamp
      pendingItems.sort((a, b) => a.timestamp - b.timestamp);

      // Process in batches
      for (let i = 0; i < pendingItems.length; i += SYNC_CONFIG.batchSize) {
        const batch = pendingItems.slice(i, i + SYNC_CONFIG.batchSize);

        for (const item of batch) {
          try {
            await this.syncItem(item);
            await indexedDBService.removeSyncItem(item.id);
            results.push({ id: item.id, success: true });
          } catch (error) {
            await this.handleSyncError(item, error);
            results.push({ id: item.id, success: false, error });
          }
        }
      }

      const successCount = results.filter((r) => r.success).length;
      return {
        success: successCount === results.length,
        message: `${successCount}/${results.length} elementos sincronizados`,
        results,
      };
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Sync a single item
   */
  private async syncItem(item: SyncQueueItem): Promise<void> {
    // Update status to processing
    await indexedDBService.updateSyncItem({ ...item, status: 'processing' });
    console.log('[Sync] Processing item:', item.type, item.id);

    switch (item.type) {
      case 'create_referidor': {
        const data = item.data as { referidor: Referidor; referidos: Referido[] };
        console.log('[Sync] Syncing referidor:', data.referidor.nombre_completo, 'with', data.referidos.length, 'referidos');
        await sheetsService.appendReferidorWithReferidos(data.referidor, data.referidos);

        // Mark as synced in local DB
        await indexedDBService.markReferidorSynced(data.referidor.id);
        if (data.referidos.length > 0) {
          await indexedDBService.markReferidosSynced(data.referidos.map((r) => r.id));
        }
        break;
      }

      case 'create_referido': {
        const referido = item.data as Referido;
        await sheetsService.appendReferido(referido);
        await indexedDBService.markReferidoSynced(referido.id);
        break;
      }

      default:
        console.warn(`Unknown sync type: ${item.type}`);
    }
  }

  /**
   * Handle sync error with exponential backoff
   */
  private async handleSyncError(item: SyncQueueItem, error: unknown): Promise<void> {
    const newRetryCount = item.retryCount + 1;
    const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
    console.error('[Sync] Error syncing item:', item.id, errorMessage, error);

    if (newRetryCount >= SYNC_CONFIG.maxRetries) {
      // Mark as failed after max retries
      await indexedDBService.updateSyncItem({
        ...item,
        status: 'failed',
        retryCount: newRetryCount,
        error: errorMessage,
      });
    } else {
      // Calculate backoff time
      const backoffTime = Math.min(
        Math.pow(SYNC_CONFIG.retryBackoffMultiplier, newRetryCount) * 1000,
        SYNC_CONFIG.maxRetryDelay
      );

      await indexedDBService.updateSyncItem({
        ...item,
        status: 'pending',
        retryCount: newRetryCount,
        nextRetry: Date.now() + backoffTime,
        error: errorMessage,
      });
    }
  }

  /**
   * Retry failed items
   */
  async retryFailed(): Promise<void> {
    const queue = await indexedDBService.getSyncQueue();
    const failedItems = queue.filter((item) => item.status === 'failed');

    for (const item of failedItems) {
      await indexedDBService.updateSyncItem({
        ...item,
        status: 'pending',
        retryCount: 0,
        error: undefined,
      });
    }
  }

  /**
   * Get failed items
   */
  async getFailedItems(): Promise<SyncQueueItem[]> {
    const queue = await indexedDBService.getSyncQueue();
    return queue.filter((item) => item.status === 'failed');
  }
}

export const syncManager = new SyncManager();
